import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import ClientSearchSelectRHF from '@/components/asyncSelect/entitySelects/ClientSearchSelectRHF.tsx';
import BookingSearchSelectRHF from '@/components/asyncSelect/entitySelects/BookingSearchSelectRHF.tsx';
import { TextareaInput } from '@/components/form/TextareaInput.tsx';
import { InfoCardList, InfoCardRow } from '@/components/ui/InfoCard.tsx';
import { EmptyValue } from '@/components/ui/EmptyValue.tsx';
import { RawTransactionListDto } from '@/features/dashboard/rawBankTransactions/types/RawTransactionListDto.ts';
import {
  matchTransactionSchema,
  MatchTransactionFormData,
  MatchTransactionInputData,
} from '@/features/dashboard/rawBankTransactions/schemas/matchTransactionSchema.ts';
import useMatchToBooking from '@/features/dashboard/finances/hooks/useMatchToBooking.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { formatPrice } from '@/utils/formatters.ts';

interface MatchTransactionModalProps {
  transaction: RawTransactionListDto;
  onClose: () => void;
}

const MatchTransactionModal = ({
  transaction,
  onClose,
}: MatchTransactionModalProps) => {
  const matchToBookingMutation = useMatchToBooking();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<MatchTransactionInputData>({
    resolver: zodResolver(matchTransactionSchema),
    defaultValues: {
      clientId: '',
      bookingId: '',
      notes: '',
    },
  });

  const clientId = watch('clientId') ?? '';

  const onSubmit = (data: MatchTransactionFormData) => {
    matchToBookingMutation.mutate(
      {
        id: transaction.id,
        request: {
          bookingId: data.bookingId,
          notes: data.notes || undefined,
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Modal onClose={onClose} isDirty={isDirty}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader title="Zakwalifikuj jako wpłatę" onClose={onClose} />
        <ModalContent>
          <div className="space-y-1 rounded-md bg-neutral-50 p-3">
            <InfoCardList className="flex-1 space-y-1">
              <InfoCardRow layout="columns" label="Kwota">
                {formatPrice(
                  transaction.amount.amount,
                  transaction.amount.currency ?? Currency.PLN,
                )}
              </InfoCardRow>
              <InfoCardRow layout="columns" label="Data transakcji">
                {dayjs(transaction.transactionDate).format('DD.MM.YYYY')}
              </InfoCardRow>
              <InfoCardRow layout="columns" label="Nadawca">
                {transaction.counterpartyName || <EmptyValue />}
              </InfoCardRow>
              {transaction.description && (
                <InfoCardRow layout="columns" label="Opis">
                  {transaction.description}
                </InfoCardRow>
              )}
            </InfoCardList>
          </div>

          <ClientSearchSelectRHF
            fieldPathName="clientId"
            control={control}
            label="Zapisujący"
            required
            error={errors.clientId?.message}
            placeholder="Wyszukaj zapisującego..."
            // Rezerwacje należą do klienta, więc zmiana klienta zeruje wybór.
            onChange={() => setValue('bookingId', '')}
          />

          <BookingSearchSelectRHF
            fieldPathName="bookingId"
            control={control}
            clientId={clientId}
            label="Rezerwacja"
            required
            error={errors.bookingId?.message}
            placeholder={
              clientId ? 'Wyszukaj rezerwację...' : 'Najpierw wybierz klienta'
            }
            hint="Cała kwota przelewu wpina się w wybraną rezerwację"
          />

          <TextareaInput
            label="Notatka"
            register={register('notes')}
            error={errors.notes?.message}
            placeholder="Opcjonalna notatka..."
            rows={2}
          />
        </ModalContent>
        <ModalFooter>
          <ActionButton tone="ghost" size="sm" type="button" onClick={onClose}>
            Anuluj
          </ActionButton>
          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={matchToBookingMutation.isPending}
          >
            Zakwalifikuj
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default MatchTransactionModal;
